// Webrtc plugin.
import { mediaActions } from '../interface/actions'
import { forwardAction } from '../../common/utils'

// Libraries.
import { takeEvery } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'

/**
 * Sets up event listeners for a Media's events.
 * The events are turned into actions and dispatched
 *    to be handled by redux elsewhere.
 * @method watchMediaEvents
 * @param  {Object} media A Media object.
 * @param  {Object} webRTC The webRTC stack.
 */
export default function * watchMediaEvents (media, webRTC) {
  /**
   * Create an event channel between the Media and redux-saga.
   * This "forces" the Media events into a redux-saga context.
   */
  const mediaChannel = eventChannel(emit => {
    return setListeners(media, emit, END)
  })
  yield takeEvery(mediaChannel, forwardAction)
}

/**
 * Function to set-up listeners for a Media object's events.
 * Contains the logic for how a webRTC event should be parsed
 *    before being sent elsewhere. Split into a stand-alone
 *    function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   media A Media object.
 * @param  {Function} emit  Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (media, emit, END = 'END') {
  // Media received a new track.
  const newTrack = ({ mediaId, trackId }) => {
    /**
     * When a new track has been added,
     * update redux state's webrtc.media.tracks array with the new trackId.
     */
    emit(mediaActions.mediaNewTrack(mediaId, { trackId }))
  }

  const trackEnded = ({ mediaId, trackId }) => {
    /**
     * When a track has ended,
     * update redux state's webrtc.media.tracks array
     * by removing the specified trackId.
     */
    emit(mediaActions.mediaTrackEnded(mediaId, { trackId }))
  }
  const mediaStopped = id => {
    /**
     * When the media is stopped, its channel should be ended (via `END`).
     * The mediaManager channel will handle updating state for this event.
     */
    emit(END)
  }

  media.on('track:new', newTrack)
  media.on('track:ended', trackEnded)
  media.on('media:stopped', mediaStopped)

  const unsubscribe = () => {
    media.off('track:new', newTrack)
    media.off('track:ended', trackEnded)
    media.off('media:stopped', mediaStopped)
  }
  return unsubscribe
}
